<div class="ai-search-dialog">
  <!-- Type Selection Step -->
  <ng-container *ngIf="step === 'select-type'">
    <div mat-dialog-title>
      <h2>Start your search</h2>
      <p class="subtitle" *ngIf="searchTerm">
        What kind of entity is <strong>{{ searchTerm }}</strong
        >?
      </p>
      <p class="subtitle" *ngIf="!searchTerm">
        What kind of entity are you looking for?
      </p>
    </div>

    <div mat-dialog-content>
      <div class="entity-type-cards">
        <div
          class="entity-type-card"
          id="ai-search-company"
          [class.selected]="entityType === 'company'"
          (click)="selectType('company')"
        >
          <span class="card-title">Company</span>
          <span class="card-subtitle"
            >Public, private or government entity</span
          >
          <span class="card-example"
            >e.g. Volkswagen, <br />
            Amazon, Apple</span
          >
        </div>
        <div
          class="entity-type-card"
          id="ai-search-topic"
          [class.selected]="entityType === 'topic'"
          (click)="selectType('topic')"
        >
          <span class="card-title">Topic</span>
          <span class="card-subtitle"
            >Theme, event or <br />issue to track</span
          >
          <span class="card-example">e.g. ESG, Inflation, <br />M&A</span>
        </div>
        <div
          class="entity-type-card"
          id="ai-search-commodity"
          [class.selected]="entityType === 'commodity'"
          (click)="selectType('commodity')"
        >
          <span class="card-title">Commodity</span>
          <span class="card-subtitle"
            >Raw material or <br />
            traded asset</span
          >
          <span class="card-example">e.g. Gold, Oil, Gas</span>
        </div>
      </div>
    </div>

    <div mat-dialog-actions>
      <button mat-raised-button color="secondary" mat-dialog-close>
        Cancel
      </button>
      <button mat-raised-button color="primary" (click)="continueToForm()">
        Continue
      </button>
    </div>
  </ng-container>

  <!-- Configure Step -->
  <ng-container *ngIf="step === 'configure'">
    <div mat-dialog-title>
      <h2 *ngIf="entityType !== 'company' || companyStep === 'form'">
        Configure Your Search
      </h2>
      <h2 *ngIf="entityType === 'company' && companyStep !== 'form'">
        Configure Your Query
      </h2>
      <p
        class="subtitle"
        *ngIf="entityType === 'company' && companyStep !== 'form'"
      >
        Enter a company name to auto-generate search parameters
      </p>
      <p
        class="subtitle"
        *ngIf="entityType === 'company' && companyStep === 'form'"
      >
        Enter company details to generate tailored search parameters
      </p>
      <p class="subtitle" *ngIf="entityType === 'topic'">
        Enter topic details to automatically generate tailored search parameters
      </p>
      <p class="subtitle" *ngIf="entityType === 'commodity'">
        Enter commodity details to automatically generate tailored search
        parameters
      </p>
    </div>

    <div mat-dialog-content>
      <!-- Company Form -->
      <app-company-form
        *ngIf="entityType === 'company'"
        [initialLegalName]="searchTerm"
        [autoSetAsNewCompany]="!!searchTerm"
        [userEmail]="userEmail"
        [project]="project"
        (validityChange)="onCompanyValidityChange($event)"
        (submitForm)="onCompanySubmit($event)"
        (navigateToEntity)="onNavigateToEntity($event)"
        (companyStepChange)="onCompanyStepChange($event)"
        (searchStateChange)="onSearchStateChange($event)"
      ></app-company-form>

      <!-- Topic Form -->
      <ng-container *ngIf="entityType === 'topic'">
        <div class="form-row">
          <mat-form-field appearance="outline">
            <mat-label>Topic</mat-label>
            <input
              matInput
              type="text"
              [(ngModel)]="topic"
              placeholder="e.g. ESG, inflation, M&A"
              required
            />
          </mat-form-field>
        </div>
        <div class="form-row">
          <mat-form-field appearance="outline">
            <mat-label>Company (optional)</mat-label>
            <input
              matInput
              type="text"
              [formControl]="entityControl"
              [matAutocomplete]="autoEntity"
              (blur)="onEntityBlur()"
            />
            <mat-autocomplete
              #autoEntity="matAutocomplete"
              [displayWith]="displayEntity"
              (optionSelected)="selectEntity($event.option.value)"
            >
              <mat-option
                *ngFor="let option of entityResults$ | async"
                [value]="option"
              >
                <div class="entity-option">
                  <span>{{ option.entity.name }}</span>
                </div>
              </mat-option>
            </mat-autocomplete>
          </mat-form-field>
        </div>
      </ng-container>

      <!-- Commodity Form -->
      <ng-container *ngIf="entityType === 'commodity'">
        <div class="form-row">
          <mat-form-field appearance="outline">
            <mat-label>Commodity</mat-label>
            <input
              matInput
              type="text"
              [(ngModel)]="commodity"
              placeholder="e.g. Gold, Oil, Gas"
              required
            />
          </mat-form-field>
        </div>
      </ng-container>

      <div class="error-message" *ngIf="errorMessage">
        {{ errorMessage }}
      </div>
    </div>

    <div mat-dialog-actions>
      <button mat-raised-button color="secondary" (click)="goBack()">
        Back
      </button>

      <!-- Company Search step: Search button -->
      <button
        *ngIf="entityType === 'company' && companyStep === 'search'"
        mat-raised-button
        color="primary"
        [disabled]="!companySearchEnabled"
        (click)="triggerCompanySearch()"
      >
        <ng-container *ngIf="!companyEnriching">Search</ng-container>
        <ng-container *ngIf="companyEnriching">
          <mat-spinner [diameter]="18" class="inline-spinner"></mat-spinner>
          Searching...
        </ng-container>
      </button>

      <!-- Company Form step: Generate AI Search -->
      <button
        *ngIf="entityType === 'company' && companyStep === 'form'"
        mat-raised-button
        color="primary"
        [disabled]="!isFormValid"
        (click)="triggerCompanySubmit()"
      >
        Generate AI Search
      </button>

      <!-- Topic / Commodity -->
      <button
        *ngIf="entityType !== 'company'"
        mat-raised-button
        color="primary"
        [disabled]="!isFormValid"
        (click)="generateQuery()"
      >
        Generate AI Search
      </button>
    </div>
  </ng-container>

  <!-- Loading Step -->
  <ng-container *ngIf="step === 'loading'">
    <div class="loading-container">
      <h2>Generating Your Search</h2>
      <p class="card-subtitle">This will take a few seconds</p>
      <mat-spinner [diameter]="60"></mat-spinner>
    </div>
  </ng-container>
</div>
